import { Injectable } from '@angular/core';

type CountryMap<T> = Record<string, T>;

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly baseKey = '[SHOP]';
  private currentCountryCode: string | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      const parts = window.location.pathname.split('/');
      const maybeCountry = parts[1];

      if (maybeCountry && maybeCountry.length === 2) {
        this.currentCountryCode = maybeCountry.toUpperCase();
        return;
      }

      try {
        const lastCountryVisitedRaw = localStorage.getItem(`${this.baseKey} lastCountryVisited`);
        const lastCountryVisited = lastCountryVisitedRaw ? JSON.parse(lastCountryVisitedRaw) : null;

        if (lastCountryVisited?.code) {
          this.currentCountryCode = lastCountryVisited.code.toUpperCase();
        }
      } catch {
        this.currentCountryCode = null;
      }
    }
  }

  setCurrentCountry(code: string | null): void {
    this.currentCountryCode = code ? code.toUpperCase() : null;
  }

  private resolveCountryCode(override?: string | null): string | null {
    return (override ?? this.currentCountryCode) ?? null;
  }

  /** Clave única por “tipo” */
  private buildUnifiedKey(key: string): string {
    return `${this.baseKey} ${key}`;
  }

  /** Antigua clave por país (para migración lazy) */
  private buildLegacyKey(key: string, countryCode: string): string {
    return `${this.baseKey}[${countryCode}] ${key}`;
  }

  private parseValue<T>(raw: string | null): T | null {
    if (raw == null) return null;
    try {
      const first = JSON.parse(raw);

      // Caso antiguo: se guardó una string JSON dentro
      if (typeof first === 'string') {
        try {
          return JSON.parse(first) as T;
        } catch {
          return first as unknown as T;
        }
      }

      return first as T;
    } catch {
      return null;
    }
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  /**
   * Lee el mapa por país almacenado en la clave unificada.
   * Devuelve {} si no existe o está corrupto.
   */
  private readCountryMap<T>(storage: Storage, unifiedKey: string): CountryMap<T> {
    const parsed = this.parseValue<unknown>(storage.getItem(unifiedKey));
    if (!this.isPlainObject(parsed)) return {};
    return parsed as CountryMap<T>;
  }

  /**
   * Escribe el mapa por país. Si queda vacío, borra la clave.
   */
  private writeCountryMap<T>(storage: Storage, unifiedKey: string, map: CountryMap<T>): void {
    const keys = Object.keys(map);
    if (keys.length === 0) {
      storage.removeItem(unifiedKey);
      return;
    }
    storage.setItem(unifiedKey, JSON.stringify(map));
  }

  /**
   * Migración lazy:
   * - Si no hay valor en la clave unificada para ese país, intenta leer la legacy key.
   * - Si la encuentra, la mueve al mapa unificado y borra la legacy key.
   */
  private migrateLegacyIfNeeded<T>(
    storage: Storage,
    key: string,
    countryCode: string
  ): void {
    const unifiedKey = this.buildUnifiedKey(key);
    const map = this.readCountryMap<T>(storage, unifiedKey);

    if (map[countryCode] != null) {
      return; // ya hay valor nuevo para este país
    }

    const legacyKey = this.buildLegacyKey(key, countryCode);
    const legacyValue = this.parseValue<T>(storage.getItem(legacyKey));
    if (legacyValue == null) {
      return;
    }

    map[countryCode] = legacyValue;
    this.writeCountryMap(storage, unifiedKey, map);

    // limpieza legacy
    storage.removeItem(legacyKey);
  }

  // =================
  // LOCAL STORAGE (por país dentro del valor)
  // =================

  getItem<T>(key: string, options?: { countryCode?: string }): T | null {
    if (typeof window === 'undefined') return null;

    const resolvedCode = this.resolveCountryCode(options?.countryCode);
    if (!resolvedCode) return null;

    const unifiedKey = this.buildUnifiedKey(key);

    // Migración automática (si venimos de legacy)
    this.migrateLegacyIfNeeded<T>(localStorage, key, resolvedCode);

    const map = this.readCountryMap<T>(localStorage, unifiedKey);
    return (map[resolvedCode] ?? null) as T | null;
  }

  setItem(key: string, value: unknown, options?: { countryCode?: string }): void {
    if (typeof window === 'undefined') return;

    const resolvedCode = this.resolveCountryCode(options?.countryCode);
    if (!resolvedCode) return;

    const unifiedKey = this.buildUnifiedKey(key);
    const map = this.readCountryMap<unknown>(localStorage, unifiedKey);

    map[resolvedCode] = value;
    this.writeCountryMap(localStorage, unifiedKey, map);
  }

  removeItem(key: string, options?: { countryCode?: string }): void {
    if (typeof window === 'undefined') return;

    const resolvedCode = this.resolveCountryCode(options?.countryCode);
    if (!resolvedCode) return;

    const unifiedKey = this.buildUnifiedKey(key);

    // también migramos antes de borrar, por si sólo existe legacy
    this.migrateLegacyIfNeeded<unknown>(localStorage, key, resolvedCode);

    const map = this.readCountryMap<unknown>(localStorage, unifiedKey);
    delete map[resolvedCode];
    this.writeCountryMap(localStorage, unifiedKey, map);
  }

  // =================
  // GLOBAL (sin país)
  // =================

  getGlobalItem<T>(key: string): T | null {
    if (typeof window === 'undefined') return null;
    const storageKey = this.buildUnifiedKey(key);
    return this.parseValue<T>(localStorage.getItem(storageKey));
  }

  setGlobalItem(key: string, value: unknown): void {
    if (typeof window === 'undefined') return;
    const storageKey = this.buildUnifiedKey(key);
    localStorage.setItem(storageKey, JSON.stringify(value));
  }

  removeGlobalItem(key: string): void {
    if (typeof window === 'undefined') return;
    const storageKey = this.buildUnifiedKey(key);
    localStorage.removeItem(storageKey);
  }
}
